import React, { ComponentProps } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import FilterChip from 'components/admin/search/FilterChip';
import AdminSearchToolsQueryHelper from './AdminSearchToolsQueryHelper';
import { AdminToolSearchRequest } from 'types/admin-tools/AdminToolSearchRequest';
import getToolTypeLabel from 'util/getToolTypeLabel';
import getToolHeadStateLabel from 'util/getToolHeadStateLabel';
import { Option } from 'types/types';

interface Props {
  filter: AdminToolSearchRequest;
  pcrOptions: Option[] | undefined;
  cpcrOptions: Option[] | undefined;
  numberOfEpdsOptions: Option[];
  toolOwnerOptions: Option[] | undefined;
}

const AdminSearchToolsRemoveFilterChips: React.FC<Props> = ({
  filter,
  pcrOptions,
  cpcrOptions,
  numberOfEpdsOptions,
  toolOwnerOptions,
}) => {
  const history = useHistory();
  const location = useLocation();

  const updateFilters = (newFilter: Partial<AdminToolSearchRequest>) => {
    const newQuery = AdminSearchToolsQueryHelper.stringify({
      ...filter,
      ...newFilter,
      pageNumber: 1,
    });
    history.push(location.pathname + newQuery);
  };

  const chipsProps: ComponentProps<typeof FilterChip>[] = [];
  if (filter.name) {
    chipsProps.push({
      text: filter.name.trim() === '' ? `"${filter.name}"` : filter.name,
      onClick: () => updateFilters({ name: '' }),
    });
  }

  if (filter.toolType) {
    chipsProps.push({
      text: getToolTypeLabel(filter.toolType),
      onClick: () => updateFilters({ toolType: null }),
    });
  }

  if (filter.pcrId) {
    if (!pcrOptions) {
      return null;
    }

    chipsProps.push({
      text: pcrOptions.find((p) => p.value === filter.pcrId)?.label ?? 'Unknown PCR',
      onClick: () => updateFilters({ pcrId: null }),
    });
  }

  if (filter.cpcrId) {
    if (!cpcrOptions) {
      return null;
    }

    chipsProps.push({
      text: cpcrOptions.find((p) => p.value === filter.cpcrId)?.label ?? 'Unknown PCR',
      onClick: () => updateFilters({ cpcrId: null }),
    });
  }

  if (filter.hasApiIntegration) {
    chipsProps.push({
      text: filter.hasApiIntegration === 'Yes' ? 'With API integration' : 'Without API integration',
      onClick: () => updateFilters({ hasApiIntegration: null }),
    });
  }

  if (filter.numberOfPublishedEpds) {
    const numberOfEpds =
      numberOfEpdsOptions.find((o) => o.value === filter.numberOfPublishedEpds)?.label ?? 'Unknown number of';
    chipsProps.push({
      text: `${numberOfEpds} published EPDs`,
      onClick: () => updateFilters({ numberOfPublishedEpds: null }),
    });
  }

  if (filter.headState) {
    chipsProps.push({
      text: getToolHeadStateLabel(filter.headState),
      onClick: () => updateFilters({ headState: null }),
    });
  }

  if (filter.toolOwnerId) {
    if (!toolOwnerOptions) {
      return null;
    }

    chipsProps.push({
      text: toolOwnerOptions.find((p) => p.value === filter.toolOwnerId)?.label ?? 'Unknown Tool Owner',
      onClick: () => updateFilters({ toolOwnerId: null }),
    });
  }

  return chipsProps.map((c, i) => <FilterChip key={i} {...c} />);
};

export default AdminSearchToolsRemoveFilterChips;
